const HerbalDrugTechUnit3Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-green-900 mb-3 border-b-2 border-slate-200 pb-2">Unit III: Herbal Cosmetics, Excipients & Formulations</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit covers the commercial and pharmaceutical applications of herbal materials in the manufacturing of cosmetics, natural excipients used in drug delivery, and the formulation of both conventional and novel herbal dosage forms.
        </p>
    </div>

    <!-- 2. Herbal Cosmetics -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">1. Herbal Cosmetics</h3>
        <p class="text-[13px] text-slate-700 mb-4">Herbal cosmetics are preparations containing phytochemicals from various botanical sources, which influence the biological functions of the skin and provide nutrients necessary for healthy skin or hair.</p>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-5">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-green-50 text-green-900">
                    <tr>
                        <th class="px-4 py-3 font-bold w-1/4">Category of Raw Material</th>
                        <th class="px-4 py-3 font-bold w-1/3">Examples (Herbal Sources)</th>
                        <th class="px-4 py-3 font-bold w-5/12">Applications in Cosmetics</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-700">
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Fixed Oils</td>
                        <td class="px-4 py-3">Almond oil, Olive oil, Castor oil, Coconut oil.</td>
                        <td class="px-4 py-3">Act as emollients (soften skin), hair conditioners, and bases for creams and lotions.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Waxes</td>
                        <td class="px-4 py-3">Beeswax, Carnauba wax.</td>
                        <td class="px-4 py-3">Used as stiffening agents in lipsticks, lip balms, and heavy creams.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Gums</td>
                        <td class="px-4 py-3">Acacia, Tragacanth, Guar gum.</td>
                        <td class="px-4 py-3">Act as thickeners, stabilizers, and suspending agents in lotions and toothpastes.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Colours</td>
                        <td class="px-4 py-3">Henna (Lawsone), Saffron, Carmine.</td>
                        <td class="px-4 py-3">Natural dyes for hair coloring (Henna) and lipsticks.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Perfumes</td>
                        <td class="px-4 py-3">Rose oil, Sandalwood oil, Lavender oil.</td>
                        <td class="px-4 py-3">Provide natural fragrance and aromatherapeutic effects in skin care products.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Protective Agents</td>
                        <td class="px-4 py-3">Aloe vera gel, Sesame oil.</td>
                        <td class="px-4 py-3">Aloe vera provides soothing and healing properties; Sesame oil acts as a natural mild sunscreen.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Bleaching Agents</td>
                        <td class="px-4 py-3">Lemon extract (Citric acid), Turmeric, Licorice.</td>
                        <td class="px-4 py-3">Used for skin lightening, removing tan, and reducing hyperpigmentation.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-green-800">Antioxidants</td>
                        <td class="px-4 py-3">Green tea extract, Vitamin E (Tocopherol).</td>
                        <td class="px-4 py-3">Prevent rancidity of oils in formulations and provide anti-aging benefits to the skin by fighting free radicals.</td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
            <h4 class="text-[14px] font-bold text-slate-800 mb-2">Common Cosmetic Products:</h4>
            <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
                <li><strong>Skin Care:</strong> Cleansing creams, moisturizers, face packs, anti-aging creams.</li>
                <li><strong>Hair Care:</strong> Shampoos (e.g., Shikakai, Reetha), Hair dyes (Henna), Hair oils (Amla, Bhringraj).</li>
                <li><strong>Oral Hygiene:</strong> Herbal toothpastes and mouthwashes (Neem, Clove oil, Peppermint).</li>
            </ul>
        </div>
    </div>

    <!-- 3. Herbal Excipients -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-3 border-b border-slate-100 pb-2">2. Herbal Excipients</h3>
        <p class="text-[13px] text-slate-700 mb-4">
            <strong>Significance:</strong> Herbal excipients are non-toxic, biodegradable, biocompatible, economical, and easily available compared to synthetic excipients. They form the bulk of the formulation and aid in the manufacturing process.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">1. Colorants</h4>
                <p class="text-[12px] text-slate-600 mt-1">Impart aesthetic appeal and help in product identification. <br><em>Examples:</em> Turmeric (Yellow), Beetroot (Red), Chlorophyll (Green).</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">2. Sweeteners</h4>
                <p class="text-[12px] text-slate-600 mt-1">Mask the bitter taste of herbal extracts, especially in syrups and lozenges. <br><em>Examples:</em> Stevia, Liquorice (Glycyrrhiza), Honey.</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">3. Binders</h4>
                <p class="text-[12px] text-slate-600 mt-1">Provide cohesive strength to powders, enabling them to be compressed into tablets. <br><em>Examples:</em> Acacia, Starch mucilage, Gelatin.</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">4. Diluents (Fillers)</h4>
                <p class="text-[12px] text-slate-600 mt-1">Increase the bulk volume of the formulation to make tablet compression practical. <br><em>Examples:</em> Lactose, Starch, Microcrystalline cellulose.</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">5. Viscosity Builders</h4>
                <p class="text-[12px] text-slate-600 mt-1">Used to thicken liquid preparations (suspensions/emulsions) to prevent settling. <br><em>Examples:</em> Tragacanth, Pectin, Agar.</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50">
                <h4 class="text-[14px] font-bold text-green-700">6. Disintegrants</h4>
                <p class="text-[12px] text-slate-600 mt-1">Help the tablet break down into smaller particles in the stomach for rapid absorption. <br><em>Examples:</em> Starch, Ispaghula husk.</p>
            </div>
            <div class="border border-slate-200 p-3 rounded-lg bg-slate-50 md:col-span-2">
                <h4 class="text-[14px] font-bold text-green-700">7. Flavors & Perfumes</h4>
                <p class="text-[12px] text-slate-600 mt-1">Enhance patient compliance by providing a pleasant taste and odor. <br><em>Examples:</em> Peppermint oil, Orange oil, Vanilla, Cinnamon.</p>
            </div>
        </div>
    </div>

    <!-- 4. Herbal Formulations & Phytosomes -->
    <div class="bg-blue-50 p-6 border border-blue-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-blue-900 mb-4 border-b border-blue-200 pb-2">3. Herbal Formulations</h3>
        
        <div class="mb-5">
            <h4 class="text-[16px] font-bold text-blue-800 mb-2">A. Conventional Herbal Formulations</h4>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Syrups:</strong> Sweet, viscous, aqueous liquid preparations containing herbal extracts (e.g., Vasaka syrup for cough).</li>
                <li><strong>Mixtures:</strong> Liquid preparations where herbal medicaments are dissolved or suspended in an aqueous vehicle.</li>
                <li><strong>Tablets:</strong> Solid unit dosage forms containing dried herbal powders or extracts mixed with suitable excipients and compressed.</li>
            </ul>
        </div>

        <div>
            <h4 class="text-[16px] font-bold text-blue-800 mb-2 flex items-center gap-2">B. Novel Dosage Forms: Phytosomes <span class="bg-blue-200 text-blue-900 px-2 py-0.5 rounded text-[11px]">IMPORTANT</span></h4>
            <p class="text-[13px] text-slate-700 mb-3 leading-relaxed">
                Many biologically active constituents of plants (like flavonoids, tannins) are water-soluble but have <strong>poor lipid solubility</strong>, resulting in very poor absorption and low bioavailability when taken orally.
            </p>
            <div class="bg-white p-4 rounded border border-blue-100 text-[13px] text-slate-700">
                <p class="font-bold text-blue-900 mb-1">What are Phytosomes?</p>
                <p class="mb-3">Phytosomes are advanced lipid-compatible molecular complexes. They are created by binding water-soluble plant extracts (e.g., Curcumin, Silymarin) to <strong>Phospholipids</strong> (like phosphatidylcholine/lecithin).</p>
                
                <p class="font-bold text-blue-900 mb-1">Advantages of Phytosomes:</p>
                <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
                    <li>The lipid outer layer enables the complex to easily cross lipid-rich cell membranes in the gut.</li>
                    <li>Massively <strong>increases the oral bioavailability</strong> of botanical extracts.</li>
                    <li>Provides protection to the active constituents from destruction by digestive enzymes and gut bacteria.</li>
                    <li>Lower doses are required to achieve the desired therapeutic effect.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default HerbalDrugTechUnit3Notes;